import React from 'react';
import { AuthenticatedImage } from './AuthenticatedImage';
import { ImageInfo } from './chatTypes';

interface ImageGridProps {
    images: ImageInfo[];
    onImageClick: (index: number) => void;
    isOwnMessage?: boolean;
    onImageLoad?: () => void;
}

export const ImageGrid: React.FC<ImageGridProps> = ({ images, onImageClick, isOwnMessage = false, onImageLoad }) => {
    const visibleImages = images.slice(0, 4);
    const remainingCount = images.length - 4;
    const alignmentClass = isOwnMessage ? 'ml-auto' : 'mr-auto';

    const getGridClassName = () => {
        const count = visibleImages.length;
        if (count === 1) return 'grid-cols-1';
        if (count === 2) return 'grid-cols-2';
        return 'grid-cols-2';
    };

    const getImageClassName = (index: number, total: number) => {
        if (total === 1) return 'col-span-1 aspect-video max-h-[300px]';
        if (total === 2) return 'col-span-1 aspect-square max-h-[200px]';
        if (total === 3) {
            if (index === 0) return 'col-span-2 aspect-video max-h-[200px]';
            return 'col-span-1 aspect-square max-h-[150px]';
        }
        return 'col-span-1 aspect-square max-h-[150px]';
    };

    return (
        <div className={`w-fit max-w-sm ${alignmentClass}`}>
            <div className={`grid ${getGridClassName()} gap-1 rounded-lg overflow-hidden`}>
                {visibleImages.map((image, index) => (
                    <div
                        key={`${image.url}-${index}`}
                        className={`relative cursor-pointer group overflow-hidden ${getImageClassName(
                            index,
                            visibleImages.length
                        )}`}
                        onClick={() => onImageClick(index)}
                    >
                        <AuthenticatedImage
                            src={image.url}
                            alt={image.alt}
                            encryptedFile={image.encryptedFile}
                            mimeType={image.mimeType}
                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                            loading="lazy"
                            onLoad={onImageLoad}
                            thumbnailSrc={image.thumbnailUrl}
                            thumbnailEncryptedFile={image.thumbnailEncryptedFile}
                            thumbnailWidth={image.thumbnailWidth}
                            thumbnailHeight={image.thumbnailHeight}
                        />

                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200" />

                        {index === 3 && remainingCount > 0 && (
                            <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                                <span className="text-white text-3xl font-semibold">
                                    +{remainingCount}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
